import React, {cloneElement, FunctionComponent, ReactElement, useCallback, useMemo, useRef, useState} from "react";
import {BizkeytechReduxActions, useBizkeytechDispatch, useBizkeytechSelector} from "../../redux";
import {Card, Dialog, DialogContent, DialogTitle, IconButton, Tooltip, Typography, Unstable_Grid2 as Grid} from "@mui/material";
import {ArrowForwardIosRounded, CloseRounded, ContentCopy, DoneAllRounded} from "@mui/icons-material";
import {
    EmailShareButton,
    FacebookShareButton,
    LinkedinShareButton,
    PinterestShareButton,
    RedditShareButton,
    TelegramShareButton,
    TwitterShareButton,
    WhatsappShareButton
} from "react-share";
import {useIsMounted} from "../../index";
import {EnvService, Utils} from "../../../core";
import {SocialIcon} from "react-social-icons";

const ShareLinkDialog: FunctionComponent = () => {
    const dispatch = useBizkeytechDispatch();
    const state = useBizkeytechSelector(state => state.dialogs?.shareLink);

    const info = useMemo(() => ({
        title: state.data?.title ?? "",
        link: state.data?.link ?? "",
        description: state.data?.description ?? "",
        source: EnvService.websiteName,
        separator: '\n\n',
        image: state.data?.image! ?? "",
    }), [state.data?.title, state.data?.link, state.data?.description, state.data?.image])

    const {hideCloseButton, disableBackdropClick, ...dialogProps} = state;

    /**
     * Closes the dialog by dispatching its relevant action to the redux store.
     */
    const closeDialog = useCallback((successful = false) => {
        if (state?.onClose) {
            state.onClose(successful ? "successful" : "leave");
        }
        dispatch(BizkeytechReduxActions.dialogs.shareLink({open: false}));
    }, [state, dispatch])

    /**
     * Closes the dialog if the values from the state do not prevent the closing of the dialog.
     */
    const onDialogClosed = useCallback((event: Object, reason: 'backdropClick' | 'escapeKeyDown' | 'closeClick') => {
        switch (reason) {
            case "backdropClick":
                if (!state.disableBackdropClick)
                    closeDialog();
                break;
            case "closeClick":
                if (!state.hideCloseButton)
                    closeDialog();
                break;
            case "escapeKeyDown":
                if (!state.disableEscapeKeyDown)
                    closeDialog();
                break;
            default:
                break;
        }
    }, [closeDialog, state.disableBackdropClick, state.disableEscapeKeyDown, state.hideCloseButton])


    return (
        <>
            <Dialog
                {...dialogProps}
                onClose={onDialogClosed}
            >
                <Grid container sx={{paddingInline: 2, paddingBlock: 1.5, gap: 1}}>
                    <Grid xs>
                        <DialogTitle fontWeight={600} variant={'body1'} sx={{padding: 0}}>
                            Share {state.data?.title ?? ""}
                        </DialogTitle>
                    </Grid>
                    <Grid xs={'auto'}>
                        <IconButton sx={{flexShrink: 1}} color={'default'} onClick={(e) => onDialogClosed(e, 'closeClick')}>
                            <CloseRounded/>
                        </IconButton>
                    </Grid>
                </Grid>
                <DialogContent sx={{display: 'flex', alignItems: 'center', flexDirection: 'column'}}>
                    <ShareLinkCard
                        title={"Share on Telegram"}
                        name={'telegram'}
                        button={<TelegramShareButton
                            url={info.link}
                            title={info.title}
                            children={<span/>}
                        />}
                    />
                    <ShareLinkCard
                        title={"Share on Whatsapp"}
                        name={'whatsapp'}
                        button={<WhatsappShareButton
                            url={info.link}
                            title={info.title}
                            separator={info.separator}
                            children={<span/>}
                        />}
                    />
                    <ShareLinkCard
                        title={"Share on Linkedin"}
                        name={'linkedin'}
                        button={<LinkedinShareButton
                            url={info.link}
                            title={info.title}
                            summary={info.description}
                            source={info.source}
                            children={<span/>}
                        />}
                    />
                    <ShareLinkCard
                        title={"Share on X"}
                        name={'x'}
                        button={<TwitterShareButton
                            url={info.link}
                            title={info.title}
                            children={<span/>}
                        />}
                    />
                    <ShareLinkCard
                        title={"Share on Facebook"}
                        name={'facebook'}
                        button={<FacebookShareButton
                            url={info.link}
                            children={<span/>}
                        />}
                    />
                    <ShareLinkCard
                        title={"Share on Reddit"}
                        name={'reddit'}
                        button={<RedditShareButton
                            url={info.link}
                            title={info.title}
                            children={<span/>}
                        />}
                    />
                    <ShareLinkCard
                        title={"Share on Pinterest"}
                        name={'pinterest'}
                        button={<PinterestShareButton
                            url={info.link}
                            description={info.description}
                            media={info.image}
                            children={<span/>}
                        />}
                    />
                    <ShareLinkCard
                        title={"Share via Email"}
                        name={'email'}
                        button={<EmailShareButton
                            url={info.link}
                            subject={info.title}
                            body={info.description}
                            separator={info.separator}
                            children={<span/>}
                        />}
                    />
                    <CopyLinkToClipboard
                        link={info.link}
                        title={"Open The Link"}
                    />
                </DialogContent>
            </Dialog>
        </>
    )
}

interface CopyLinkToClipboardProps {
    link: string,
    title?: string,
}

const CopyLinkToClipboard: FunctionComponent<CopyLinkToClipboardProps> = (props) => {
    const [copied, setCopied] = useState(false);
    const isMounted = useIsMounted();

    /**
     * Copies the ticket's link to the clipboard.
     */
    const copyToClipboard = useCallback(async () => {
        const result = await Utils.copyToClipboard(props.link);
        if (result) {
            setCopied(true);
            setTimeout(() => {
                if (!isMounted())
                    return;
                setCopied(false);
            }, 3000)
        }
    }, [props.link, isMounted])

    return (
        <>
            <Card
                variant={'outlined'}
                sx={{
                    width: '100%',
                    marginBlock: 2,
                    paddingBlock: 1,
                    paddingInline: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    transition: 'background-color 0.3s',
                    "&:hover": {
                        backgroundColor: 'background.default',
                    },
                }}
            >
                <Typography
                    variant={'body2'}
                    component={'a'}
                    color={'text.primary'}
                    fontWeight={600}
                    href={props.link}
                    target={'_blank'}
                >
                    {props.title ?? props.link}
                </Typography>
                <Tooltip
                    title={copied ? "Link copied" : "Copy link"}
                    placement={'left'}
                    arrow
                    enterDelay={100}
                    enterTouchDelay={100}
                >
                    <IconButton
                        color={copied ? 'paper.success' : 'paper.primary'}
                        size={'medium'}
                        sx={{backgroundColor: 'background.paper'}}
                        onClick={copyToClipboard}
                    >
                        {
                            copied
                                ? <DoneAllRounded/>
                                : <ContentCopy/>
                        }
                    </IconButton>
                </Tooltip>
            </Card>
        </>
    );

}

interface ShareLinkCardProps {
    button: ReactElement;
    title: string;
    name: string;
}

const ShareLinkCard: FunctionComponent<ShareLinkCardProps> = (props) => {
    const buttonRef = useRef<HTMLElement | null>(null);

    /**
     * Clicks the button when the card is clicked to invoke its functionality.
     */
    const onClick = useCallback(() => {
        if (buttonRef.current)
            buttonRef.current.click();
    }, [])

    return (
        <>
            <Card
                variant={'elevation'}
                onClick={onClick}
                sx={{
                    padding: 2,
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    boxShadow: 'none',
                    transition: 'background-color 0.3s',
                    gap: 2,
                    "&:hover": {
                        backgroundColor: 'background.default',
                    },
                }}
            >
                {
                    cloneElement(
                        props.button,
                        {ref: buttonRef},
                        <SocialIcon
                            as={'div'}
                            network={props.name}
                            style={{width: 30, height: 30}}
                        />
                    )
                }
                <Typography variant={'body2'} fontWeight={600} sx={{flexGrow: 1}}>
                    {props.title}
                </Typography>
                <ArrowForwardIosRounded sx={{padding: 0.5}}/>
            </Card>
        </>
    );
}

export default ShareLinkDialog;
